import { faBars, faEye, faEyeLowVision, faPencil, faRotateLeft } from "@fortawesome/pro-light-svg-icons";
import { faFlag } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, IconButton, useTheme } from "@mui/material";
import { type ReactNode, memo, useState } from "react";
import { Draggable } from "react-beautiful-dnd";
import { isMobile } from "react-device-detect";

import type { EXPERIENCE_PRIMARY_FILTER } from "@/constants";

import MenuTooltip from "./MenuTooltip";
import { StyledDragHandle } from "./styled";

interface MenuAccordionItemProps {
  readonly hidden?: boolean;
  readonly icon?: ReactNode;
  readonly id: number;
  readonly index: number;
  readonly name: string;
  readonly onRename?: (value: string) => void;
  readonly onToggleSelection?: () => void;
  readonly onToggleVisibility?: () => void;
  readonly selected: boolean;
  readonly showDragHandle?: boolean;
  readonly showRenameButton?: boolean;
  readonly showVisibilityButton?: boolean;
  readonly locked?: boolean;
  readonly context?: string;
  readonly menuRefs: {
    setRef: (key: string) => (element: HTMLElement | null) => void;
  };
  readonly typeFilterId: number | null;
  readonly assetCategoryFilterId: number | null;
  readonly primaryFilter: (typeof EXPERIENCE_PRIMARY_FILTER)[keyof typeof EXPERIENCE_PRIMARY_FILTER];
  readonly showStartingPointFlag?: boolean;
}

const styles = {
  container: {
    borderRadius: "8px",
    color: "white",
    display: "flex",
    fontSize: "16px",
    height: "40px",
    padding: "0px 8px !important",
    alignItems: "center",
    cursor: "pointer",
    "& .showOnHover": {
      opacity: 0,
      position: "absolute",
      width: "0",
      height: "0",
      "&:focus": {
        position: "relative",
        opacity: "1",
        width: "auto",
        height: "auto",
      },
    },
    "&:hover": {
      "& .showOnHover": {
        position: "relative",
        opacity: "1",
        width: "auto",
        height: "auto",
      },
    },
  },
  selected: (color: string) => ({
    "& span": {
      color,
    },
    "& svg path": {
      fill: color,
    },
  }),
  buttonIcon: {
    alignItems: "center",
    justifyContent: "center",
    display: "flex",
    fontSize: "12px",
    height: "100%",
    minWidth: "24px",
    width: "24px",
    cursor: "pointer",
    marginLeft: "8px",
    padding: "0px",
  },
  buttonName: {
    flex: 1,
    fontSize: 16,
    justifyContent: "flex-start",
    flexDirection: "row",
    textAlign: "left",
    textWrap: "nowrap",
    cursor: "pointer",
    overflow: "hidden",
    display: "flex",
    color: "#fff",
    padding: "0px",
    textTransform: "none",
    "& > svg": {
      alignItems: "center",
      display: "flex",
      flex: "none",
      fontSize: "16px",
      justifyContent: "center",
      width: "16px",
      height: "100%",
      marginRight: "8px",
    },
  },
  input: {
    background: "transparent",
    border: 0,
    borderBottom: "1px solid white",
    color: "white",
    flex: 1,
    fontSize: 16,
    outline: 0,
    width: "100%",
  },
};

function MenuAccordionItem(props: MenuAccordionItemProps) {
  const {
    name,
    icon,
    selected,
    index,
    id,
    showDragHandle,
    showVisibilityButton,
    hidden,
    showRenameButton,
    onToggleVisibility,
    onToggleSelection,
    onRename,
    locked,
    context,
    menuRefs,
    typeFilterId,
    assetCategoryFilterId,
    primaryFilter,
    showStartingPointFlag = false,
  } = props;
  const [renaming, setRenaming] = useState(false);
  const [inputName, setInputName] = useState(name || "");
  const [error, setError] = useState("");
  const theme = useTheme();
  const color = (theme as unknown as { primaryColor: string }).primaryColor;

  return (
    <Draggable draggableId={String(id)} index={index} key={id} isDragDisabled={locked}>
      {(providedDraggable) => (
        <Box
          sx={[styles.container, { height: "40px" }]}
          {...providedDraggable.draggableProps}
          ref={providedDraggable.innerRef}
          tabIndex={-1}
        >
          {renaming && !locked ? (
            <Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box sx={{ flex: 1, marginRight: "8px" }}>
                  <input
                    data-testid="item-input"
                    style={styles.input}
                    value={inputName}
                    onChange={(e) => {
                      setInputName(e.target.value);
                      !e.target.value && setError("Too short");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !error) {
                        onRename?.(inputName);
                        setRenaming(false);
                      }
                    }}
                  />
                </Box>

                <IconButton
                  data-testid="expand-button"
                  sx={[styles.buttonIcon, { fontSize: 16 }]}
                  onClick={() => setRenaming(false)}
                >
                  <FontAwesomeIcon icon={faRotateLeft} />
                </IconButton>
              </Box>
              <Box sx={{ fontSize: 12, color: "#ff0000" }}>{error}</Box>
            </Box>
          ) : (
            <MenuTooltip isMobile={isMobile} text={name} section="accordion-item">
              <Button
                ref={menuRefs.setRef(`filter-menu-${primaryFilter}-${assetCategoryFilterId}-${typeFilterId}`)}
                data-testid="item-name-button"
                data-analytics={`vb-experience-${context}-item`}
                sx={[styles.buttonName, { minWidth: 0, alignItems: "center" }, selected && styles.selected(color)]}
                onClick={onToggleSelection}
              >
                <StyledDragHandle
                  $size={12}
                  $color={color}
                  $selected={selected}
                  data-testid="item-drag-handle"
                  style={{ display: showDragHandle ? "flex" : "none" }}
                  {...(!locked ? providedDraggable.dragHandleProps : {})}
                >
                  {showStartingPointFlag ? (
                    <>
                      <FontAwesomeIcon icon={faFlag} className="flag-icon" />
                      <FontAwesomeIcon icon={faBars} className="drag-icon" />
                    </>
                  ) : (
                    <FontAwesomeIcon icon={faBars} className="drag-icon" />
                  )}
                </StyledDragHandle>

                {icon}

                <Box
                  component="span"
                  sx={[
                    { flex: 1, overflow: "hidden", textWrap: "nowrap", textOverflow: "ellipsis" },
                    hidden ? { opacity: 0.5 } : {},
                  ]}
                >
                  {name}
                </Box>

                {showRenameButton && (
                  <Box
                    data-testid="item-rename-button"
                    className="showOnHover"
                    sx={styles.buttonIcon}
                    onClick={(e) => {
                      if (locked) return;

                      setRenaming(true);
                      e.stopPropagation();
                    }}
                  >
                    <FontAwesomeIcon icon={faPencil} />
                  </Box>
                )}

                {showVisibilityButton && (
                  <Box
                    data-testid="item-visibility-button"
                    className="showOnHover"
                    sx={styles.buttonIcon}
                    onClick={(e) => {
                      onToggleVisibility?.();
                      e.stopPropagation();
                    }}
                  >
                    <FontAwesomeIcon icon={hidden ? faEyeLowVision : faEye} />
                  </Box>
                )}
              </Button>
            </MenuTooltip>
          )}
        </Box>
      )}
    </Draggable>
  );
}

export default memo(MenuAccordionItem);
